import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, ArrowsClockwise, Trash, CheckCircle, Warning } from "@phosphor-icons/react";

const BIN_LABEL = {
  restmuell: { de: "Restmüll (schwarz)", color: "#374151" },
  biomuell: { de: "Biotonne (braun)", color: "#8B5A2B" },
  papier: { de: "Altpapier (blau)", color: "#2563EB" },
  gelb: { de: "Gelber Sack (gelb)", color: "#F59E0B" },
  unknown: { de: "Abfuhr", color: "#6B7280" },
};

/**
 * AbfallPanel — customer self-service. Konfiguriert ICS-URL und zeigt die
 * nächsten Müllabfuhr-Termine an.
 * Props: brandColor
 */
export default function AbfallPanel({ brandColor = "#2A86A8" }) {
  const [icsUrl, setIcsUrl] = useState("");
  const [data, setData] = useState({ ics_url: null, synced_at: null, events: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/customer/me/abfall");
      setData(r.data);
      setIcsUrl(r.data.ics_url || "");
    } catch (e) { if (e?.response?.status !== 404) console.warn("[AbfallPanel] load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.patch("/customer/me/abfall", { ics_url: icsUrl || null });
      setMsg("URL gespeichert");
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const sync = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.patch("/customer/me/abfall", { ics_url: icsUrl || null });
      const r = await api.post("/customer/me/abfall/sync");
      setMsg(`${r.data.count} Termine synchronisiert`);
      load();
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!confirm("Abfallkalender wirklich entfernen?")) return;
    setBusy(true);
    try {
      await api.patch("/customer/me/abfall", { ics_url: null });
      setIcsUrl(""); load();
    } finally { setBusy(false); }
  };

  const upcoming = (data.events || []).filter((e) => new Date(e.datetime) >= new Date(Date.now() - 86400000)).slice(0, 8);

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6" data-testid="abfall-panel">
      <div className="flex items-center gap-2 mb-1">
        <Calendar size={20} weight="bold" style={{ color: brandColor }} />
        <h3 className="font-bold text-lg">Müllabfuhrkalender</h3>
      </div>
      <p className="text-sm text-zinc-500 mb-4">
        Hinterlegen Sie den iCal-Link Ihrer Gemeinde (z. B. von <a href="https://www.abfall.io" target="_blank" rel="noreferrer" className="underline">abfall.io</a> oder der städtischen Abfallwirtschaft).
        Wir planen Ihre Tonnenreinigung dann automatisch ein bis zwei Tage nach der Abfuhr.
      </p>

      <div className="flex gap-2 mb-2">
        <Input
          type="url"
          className="h-11 rounded-md flex-1"
          placeholder="https://www.abfall.io/...ics"
          value={icsUrl}
          onChange={(e) => setIcsUrl(e.target.value)}
          data-testid="abfall-url-input"
        />
        <Button onClick={sync} disabled={busy || !icsUrl} className="rounded-md text-white" style={{ background: brandColor }} data-testid="abfall-sync-btn">
          <ArrowsClockwise size={14} weight="bold" className="mr-1.5" /> Synchronisieren
        </Button>
        {data.ics_url && (
          <Button onClick={clear} variant="outline" className="rounded-md" data-testid="abfall-clear-btn"><Trash size={14} weight="bold" /></Button>
        )}
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 rounded-md p-2.5 mt-2" data-testid="abfall-error">{err}</div>}
      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 rounded-md p-2.5 mt-2" data-testid="abfall-msg">{msg}</div>}

      {data.synced_at && (
        <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1">
          <CheckCircle size={12} weight="bold" style={{ color: "#16A34A" }} />
          Zuletzt synchronisiert: {new Date(data.synced_at).toLocaleString("de-DE")}
        </p>
      )}

      {upcoming.length > 0 ? (
        <div className="mt-5 pt-5 border-t border-black/5">
          <h4 className="font-semibold text-sm mb-3">Nächste Abfuhrtermine</h4>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="abfall-events">
            {upcoming.map((e, idx) => {
              const cfg = BIN_LABEL[e.bin_type] || BIN_LABEL.unknown;
              const d = new Date(e.datetime);
              const isToday = d.toDateString() === new Date().toDateString();
              const isTomorrow = d.toDateString() === new Date(Date.now() + 86400000).toDateString();
              return (
                <div key={`${e.datetime}-${e.bin_type}`} className="flex items-center gap-3 p-3 rounded-lg border border-black/5" data-testid={`abfall-event-${idx}`}>
                  <div className="w-12 h-12 rounded-md flex flex-col items-center justify-center text-white" style={{ background: cfg.color }}>
                    <div className="text-xs font-bold leading-none">{d.toLocaleDateString("de-DE", { day: "2-digit" })}</div>
                    <div className="text-[9px] uppercase mt-0.5">{d.toLocaleDateString("de-DE", { month: "short" })}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{cfg.de}</div>
                    <div className="text-xs text-zinc-500">{isToday ? "Heute" : isTomorrow ? "Morgen" : d.toLocaleDateString("de-DE", { weekday: "long" })}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : data.synced_at ? (
        <p className="text-sm text-zinc-400 mt-4">Keine anstehenden Termine in den nächsten 90 Tagen.</p>
      ) : null}
    </div>
  );
}
